import { useAuth } from "@/contexts/keycloak";
import { getKeycloak, getToken } from "@/lib/keycloak";
import React, { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Socket, io } from "socket.io-client";
import {
  type PermissionsSyncPayload,
  type IEntityPermissionsPublic,
  RPC_EVENT_PERMISSIONS_SYNC_IDENT,
  ENTITY_PERMISSIONS_SERVICE_IDENT,
} from "@bloomstack/apis/services/IEntityPermissions";
import { useService, useServiceEventListener } from "@/hooks/useService";
import { useAppSession } from "@/contexts/appSession";

export type ConnectionStatus = 'connected' | 'reconnecting' | 'browser-offline' | 'server-unreachable' | 'unstable';

export type ConnectionEvent = {
  type: 'connect' | 'disconnect' | 'connect_error';
  timestamp: number;
  detail?: string;
};

/** Time after reconnection before the ring fades (ms) */
const RING_FADE_DELAY = 30000;

/** External endpoints to ping when determining if the network or server is down */
const EXTERNAL_PING_URLS = [
  'https://cloudflare.com/cdn-cgi/trace',
  'https://1.1.1.1/cdn-cgi/trace',
  'https://www.google.com',
];

/**
 * Ping external endpoints to determine if the user has internet access.
 * Returns true if at least one endpoint responds.
 */
async function checkExternalConnectivity(): Promise<boolean> {
  try {
    const results = await Promise.allSettled(
      EXTERNAL_PING_URLS.map(url =>
        fetch(url, { mode: 'no-cors', cache: 'no-store' })
      )
    );
    return results.some(r => r.status === 'fulfilled');
  } catch {
    return false;
  }
}

export interface ISocketIOContext {
  socket: Socket | undefined;
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  reconnectAttempt: number;
  lastError: string | undefined;
  connectionHistory: ConnectionEvent[];
  reconnect: () => void;
  /** Whether the ring should still be visible after a recent reconnection */
  showRecentReconnect: boolean;
  /**
   * Whether this socket has ever successfully connected during the current page
   * session. Resets to false only on a fresh page load (new ref). Used to gate
   * same-session re-subscribe passes: a reconnect after an initial connect is a
   * genuine re-subscribe, whereas a first-ever connect (page load) is not.
   */
  hasEverConnected: boolean;
}

export const SocketIOContext = createContext<ISocketIOContext | null>(null);

/**
 * Hook to access the SocketIO context.
 * Throws if used outside of SocketIOProvider.
 */
export const useSocketIOContext = () => {
  const context = useContext(SocketIOContext);
  if (!context) {
    throw new Error("useSocketIOContext must be used within a SocketIOProvider");
  }
  return context;
};

/**
 * Hook to optionally access the SocketIO context.
 * Returns a fallback with empty permissions if no provider is present.
 * Useful for components that may render with or without socket connection.
 */
export const useSocketIOContextOptional = (): ISocketIOContext => {
  const context = useContext(SocketIOContext);
  if (!context) {
    return {
      socket: undefined,
      isConnected: false,
      connectionStatus: 'reconnecting',
      reconnectAttempt: 0,
      lastError: undefined,
      connectionHistory: [],
      reconnect: () => {},
      showRecentReconnect: false,
      hasEverConnected: false,
    };
  }
  return context;
};

export type SocketIOProviderProps = {
  children: ReactNode;
  /** Disable socket connection - provides context with socket: undefined, isConnected: false */
  disabled?: boolean;
};

export const SocketIOProvider: FC<SocketIOProviderProps> = (props) => {
  const { token, isAuthenticated } = useAuth();
  const { user, clearSession } = useAppSession();
  const socketRef = useRef<Socket | undefined>(undefined);
  const hasEverConnected = useRef(false);
  const [connectCount, setConnectCount] = useState(0);

  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('reconnecting');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [lastError, setLastError] = useState<string | undefined>(undefined);
  const [connectionHistory, setConnectionHistory] = useState<ConnectionEvent[]>([]);
  const [showRecentReconnect, setShowRecentReconnect] = useState(false);
  const ringFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Instability detection parameters
  const INSTABILITY_WINDOW_MS = 30_000;   // 30-second rolling window
  const INSTABILITY_THRESHOLD = 3;         // 3+ disconnects in window → unstable
  const STABILIZATION_DELAY_MS = 30_000;  // 30s with no disconnects → back to connected
  const stabilizationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep history accessible in event handlers without stale closure
  const connectionHistoryRef = useRef<ConnectionEvent[]>([]);
  useEffect(() => { connectionHistoryRef.current = connectionHistory; }, [connectionHistory]);

  // Track if we have a token (boolean) to avoid reconnecting on every token refresh
  const hasToken = !!token && isAuthenticated;

  const addHistoryEvent = useCallback((type: ConnectionEvent['type'], detail?: string) => {
    setConnectionHistory(prev => [...prev.slice(-49), { type, timestamp: Date.now(), detail }]);
  }, []);

  // Update socket auth when token changes (for future reconnects), but don't disconnect
  useEffect(() => {
    if (socketRef.current && token) {
      socketRef.current.auth = { token };
    }
  }, [token]);

  // Browser online/offline detection
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onOnline = () => {
      setConnectionStatus(prev => prev === 'browser-offline' ? 'reconnecting' : prev);
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.disconnect().connect();
      }
    };

    const onOffline = () => {
      if (!isConnected) {
        setConnectionStatus('browser-offline');
      }
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [isConnected]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.connect();
    }
  }, []);

  // Manage socket connection - only connect/disconnect based on token existence, not value
  useEffect(() => {
    if ( typeof window === "undefined" ) {
      return;
    }

    if (props.disabled) {
      console.warn('SocketIOProvider: disabled mode - socket connection skipped');
      return;
    }

    if (!hasToken) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = undefined;
        setIsConnected(false);
        setConnectionStatus('reconnecting');
        clearSession();
      }
      return;
    }

    // Only create socket if we don't have one
    if (!socketRef.current) {
      let cancelled = false;
      let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
      
      // Define handlers at this scope so cleanup can access them
      const onConnect = () => {
        // noop — server_ready is what matters
      };

      const onServerReady = () => {
        // Cancel pending disconnect if we reconnected in time
        if (disconnectTimer) {
          clearTimeout(disconnectTimer);
          disconnectTimer = null;
        }
        hasEverConnected.current = true;
        setIsConnected(true);
        setReconnectAttempt(0);
        setLastError(undefined);
        addHistoryEvent('connect');

        // If we were previously disconnected, show the ring for a fade period
        setConnectionStatus(prev => {
          if (prev !== 'connected' && prev !== 'unstable') {
            setShowRecentReconnect(true);
            if (ringFadeTimerRef.current) clearTimeout(ringFadeTimerRef.current);
            ringFadeTimerRef.current = setTimeout(() => {
              setShowRecentReconnect(false);
              ringFadeTimerRef.current = null;
            }, RING_FADE_DELAY);
          }
          // Check for instability: count disconnect events in the rolling window.
          // Use the ref snapshot (includes the just-added 'connect' event) to avoid
          // stale closure — history state update is async.
          const now = Date.now();
          const windowStart = now - INSTABILITY_WINDOW_MS;
          const recentDisconnects = connectionHistoryRef.current.filter(
            e => e.type === 'disconnect' && e.timestamp >= windowStart
          ).length;

          if (recentDisconnects >= INSTABILITY_THRESHOLD) {
            // Connection is flapping — mark unstable and start stabilization timer
            if (stabilizationTimerRef.current) clearTimeout(stabilizationTimerRef.current);
            stabilizationTimerRef.current = setTimeout(() => {
              stabilizationTimerRef.current = null;
              setConnectionStatus('connected');
            }, STABILIZATION_DELAY_MS);
            return 'unstable';
          }

          return 'connected';
        });

        setConnectCount(count => (count + 1) % 1000);
      };

      const onDisconnect = async (reason: Socket.DisconnectReason) => {
        addHistoryEvent('disconnect', reason);

        // "io server disconnect" means the server kicked us (likely auth failure).
        // Socket.IO will NOT auto-reconnect for this reason.
        // We need to refresh the token and manually reconnect.
        if (reason === 'io server disconnect') {
          try {
            const keycloak = getKeycloak();
            await keycloak.updateToken(-1); // Force refresh
            const freshToken = getToken();
            if (freshToken && socketRef.current) {
              socketRef.current.auth = { token: freshToken };
              socketRef.current.connect();
              return; // Skip the debounce — we're handling it
            }
          } catch (err) {
            console.error('[SocketIO] Token refresh failed, cannot reconnect:', err);
            // Session is probably expired — Keycloak provider will handle the modal
          }
        }

        // Reset stabilization timer — a new disconnect means we're still flapping
        if (stabilizationTimerRef.current) {
          clearTimeout(stabilizationTimerRef.current);
          stabilizationTimerRef.current = null;
        }

        // Debounce the disconnected state — if we reconnect within 1.5s,
        // consumers never see isConnected flip to false, avoiding the
        // cascade of re-subscriptions and permission re-requests.
        if (disconnectTimer) clearTimeout(disconnectTimer);
        disconnectTimer = setTimeout(() => {
          disconnectTimer = null;
          setIsConnected(false);
          setConnectionStatus(prev => {
            if (prev === 'unstable') return prev; // Keep unstable state visible
            return navigator.onLine ? 'reconnecting' : 'browser-offline';
          });
        }, 1500);
      };

      const onConnectError = async (err: Error) => {
        const errorMsg = err.message || 'Unknown error';
        setReconnectAttempt(prev => prev + 1);
        setLastError(errorMsg);
        addHistoryEvent('connect_error', errorMsg);

        // Determine if it's our server or their network
        if (!navigator.onLine) {
          setConnectionStatus('browser-offline');
        } else {
          const hasInternet = await checkExternalConnectivity();
          // Guard: don't overwrite 'connected' if we reconnected while pinging
          setConnectionStatus(prev => {
            if (prev === 'connected') return prev;
            return hasInternet ? 'server-unreachable' : 'browser-offline';
          });
        }
      };

      const initializeConnection = async () => {
        
        // Establish session cookie BEFORE socket connection
        // This ensures authenticated image requests won't fail due to missing cookie
        try {
          await fetch('/api/auth/session', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
        } catch (err) {
          console.warn('[SocketIO] Failed to establish session cookie:', err);
          // Continue anyway - socket auth will still work via JWT
        }

        // Check if effect was cleaned up while we were waiting
        if (cancelled) {
          return;
        }
        socketRef.current = io({
          withCredentials: true,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          auth: {
            token,
          },
        });

        socketRef.current.on("connect", onConnect);
        socketRef.current.on("server_ready", onServerReady);
        socketRef.current.on("disconnect", onDisconnect);
        socketRef.current.on("connect_error", onConnectError);
      };

      initializeConnection();

      return () => {
        cancelled = true;
        if (disconnectTimer) {
          clearTimeout(disconnectTimer);
          disconnectTimer = null;
        }
        if (socketRef.current) {
          socketRef.current.off("connect", onConnect);
          socketRef.current.off("server_ready", onServerReady);
          socketRef.current.off("disconnect", onDisconnect);
          socketRef.current.off("connect_error", onConnectError);
          socketRef.current.disconnect();
          socketRef.current = undefined;
          setIsConnected(false);
          clearSession();
        }
      };
    }
  }, [hasToken]); // Only depends on token existence, not value

  // Clean up ring fade timer on unmount
  useEffect(() => {
    return () => {
      if (ringFadeTimerRef.current) clearTimeout(ringFadeTimerRef.current);
    };
  }, []);
  
  // create the pubsub api
  const api: ISocketIOContext = useMemo(() => ({
    socket: hasEverConnected.current || isConnected ? socketRef.current : undefined,
    isConnected,
    connectionStatus,
    reconnectAttempt,
    lastError,
    connectionHistory,
    reconnect,
    showRecentReconnect,
    hasEverConnected: hasEverConnected.current,
  }), [
    isConnected,
    socketRef.current,
    connectionStatus,
    reconnectAttempt,
    lastError,
    connectionHistory,
    reconnect,
    showRecentReconnect,
    hasEverConnected.current,
  ]);

  return (
    <SocketIOContext.Provider value={api}>
      <SessionSync />
      {props.children}
    </SocketIOContext.Provider>
  )
}

const SessionSync: FC = () => {
  const { isConnected } = useSocketIOContext();
  const { setSession } = useAppSession();
  const permsService = useService<IEntityPermissionsPublic>(ENTITY_PERMISSIONS_SERVICE_IDENT).withPriority("high");

  // Explicitly request session when connected
  useEffect(() => {
    if (!isConnected) return;

    permsService.requestPermissions().then((result: any) => {
      if (result.success) {
        setSession(result.result);
      } else {
        console.error('[SessionSync] Failed to get session:', result.error);
      }
    });
  }, [isConnected]);

  // Keep event listener for real-time updates when permissions change
  useServiceEventListener(RPC_EVENT_PERMISSIONS_SYNC_IDENT, (payload: PermissionsSyncPayload) => {
    setSession(payload);
  }, [isConnected]);

  return null;
}